import React, { useEffect, useRef } from 'react';

/**
 * SuggestionChips
 * ─────────────────────────────────────────────────────────────
 * Renders 2–3 suggestion chips below a FragHejVi assistant message.
 *
 * Props:
 *   suggestions  array   RAG response suggestions field
 *                        [{ type: 'question'|'open_document'|'open_video', label: string }]
 *   links        array   RAG response links field (for deep-link resolution)
 *                        [{ label, url, source_type }]
 *   onQuestion   fn      (label: string) => void  — sends label as new user message
 *   onNavigate   fn      (url: string) => void    — navigates to element/document
 *   language     string  current UI language code ('de', 'en', ...)
 * ─────────────────────────────────────────────────────────────
 */

const TYPE_CONFIG = {
  question: {
    icon: '💬',
    color: '#FF6407',
    bg: '#FFF7ED',
    border: '#FDBA74',
    hoverBg: '#FFF0E0',
  },
  open_document: {
    icon: '📄',
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#BFDBFE',
    hoverBg: '#DBEAFE',
  },
  open_video: {
    icon: '▶',
    color: '#7C3AED',
    bg: '#F5F3FF',
    border: '#DDD6FE',
    hoverBg: '#EDE9FE',
  },
  // Varianten-Auswahl (OVL): Nutzer wählt konkrete Produktvariante
  select_variant: {
    icon: '🔍',
    color: '#059669',
    bg: '#ECFDF5',
    border: '#6EE7B7',
    hoverBg: '#D1FAE5',
  },
  // Near-miss: ähnliches Produkt gefunden, Nutzer kann es annehmen
  suggest_similar: {
    icon: '💡',
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FCD34D',
    hoverBg: '#FEF3C7',
  },
  // Kollegen fragen: Frage an Admin weiterleiten
  ask_colleague: {
    icon: '👷',
    color: '#6B7280',
    bg: '#F9FAFB',
    border: '#E5E7EB',
    hoverBg: '#F3F4F6',
  },
};

const INTRO_LABELS = {
  de: 'Mögliche nächste Schritte:',
  en: 'You might want to:',
  fr: 'Vous pourriez aussi :',
  es: 'También podrías:',
  pl: 'Możesz też:',
  ru: 'Вы также можете:',
  tr: 'Şunları da yapabilirsiniz:',
  ro: 'Poți continua cu:',
  hr: 'Možete i:',
  ar: ':يمكنك أيضاً',
};

// Intro-Label wenn HejVi selbst nachfragt (is_rag=false)
const CLARIFICATION_LABELS = {
  de: 'Bitte beantworte im Eingabefeld:',
  en: 'Please answer in the input below:',
  fr: 'Merci de répondre dans le champ ci-dessous :',
  es: 'Por favor responde en el campo de abajo:',
  pl: 'Proszę odpowiedz w polu poniżej:',
  ru: 'Пожалуйста, ответьте в поле ввода ниже:',
  tr: 'Lütfen aşağıdaki alana cevap yazın:',
  ro: 'Te rugăm să răspunzi în câmpul de mai jos:',
  hr: 'Molimo odgovorite u polje ispod:',
  ar: ':يرجى الإجابة في حقل الإدخال أدناه',
};

export default function SuggestionChips({
  suggestions = [],
  links = [],
  onQuestion,
  onNavigate,
  language = 'de',
  isRag = true,
}) {
  const containerRef = useRef(null);

  // Build a map from source_type → first matching deep_link (Fallback)
  const linkMap = {};
  for (const l of links) {
    if (l.url && l.source_type && !linkMap[l.source_type]) {
      linkMap[l.source_type] = l.url;
    }
  }

  /**
   * Findet die beste URL für einen open_document-Chip via Keyword-Matching.
   * Verhindert dass ein zufällig hoch gerankter fremder Treffer (z.B. Porsche)
   * als Navigationsziel für einen thematisch unverwandten Chip genommen wird.
   */
  const findBestDocumentUrl = (chipLabel) => {
    const docLinks = links.filter(l =>
      l.source_type === 'document' || l.source_type === 'quiz'
    );
    if (!docLinks.length) return null;
    if (docLinks.length === 1) return docLinks[0].url;

    const stopwords = new Set(['ansehen', 'oeffnen', 'zeigen', 'dokument', 'video',
      'mehr', 'ueber', 'from', 'open', 'view', 'show', 'the', 'eine', 'einen']);
    const words = chipLabel
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length >= 4 && !stopwords.has(w));

    if (words.length) {
      const scored = docLinks.map(l => {
        const lbl = (l.label || '').toLowerCase();
        const matches = words.filter(w => lbl.includes(w)).length;
        return { url: l.url, score: matches };
      });
      scored.sort((a, b) => b.score - a.score);
      if (scored[0].score > 0) return scored[0].url;
    }
    return docLinks[0].url;
  };

  // Staggered entrance animation on mount
  useEffect(() => {
    if (!containerRef.current) return;
    const chips = containerRef.current.querySelectorAll('[data-chip]');
    chips.forEach((chip, i) => {
      chip.style.opacity = '0';
      chip.style.transform = 'translateY(6px)';
      setTimeout(() => {
        chip.style.transition = 'opacity 0.22s ease, transform 0.22s ease';
        chip.style.opacity = '1';
        chip.style.transform = 'translateY(0)';
      }, 80 + i * 60);
    });
  }, [suggestions]);

  if (!suggestions || suggestions.length === 0) return null;

  // Im Clarification-Modus (is_rag=false) zeigt HejVi Rückfragen AN den Nutzer —
  // question-Chips sind dann Hinweise zum Eintippen, keine klickbaren Aktionen.
  const isClarificationMode = !isRag;
  const introLabel = isClarificationMode
    ? (CLARIFICATION_LABELS[language] || CLARIFICATION_LABELS.de)
    : (INTRO_LABELS[language] || INTRO_LABELS.de);

  const handleChipClick = (s) => {
    // Im Clarification-Modus sind question-Chips nicht klickbar —
    // der Nutzer soll seine Antwort selbst eintippen.
    if (s.type === 'question' && isClarificationMode) return;
    if (s.type === 'question') {
      onQuestion?.(s.label);
      return;
    }
    // select_variant: sendet s.query (z.B. "Porsche 911 993 — Wie wechsle ich den Zahnriemen?")
    // statt s.label (z.B. "Porsche 911 993 Cabrio (Reparaturanleitung)")
    if (s.type === 'select_variant') {
      onQuestion?.(s.query || s.label);
      return;
    }
    // suggest_similar: Near-miss-Angebot annehmen.
    // label = lesbarer Chip-Text ("Ja, zeig mir Porsche 911 G-Modell")
    // query = optimierter Retrieval-String ("Porsche 911 G-Modell — Wie...?")
    // Wir zeigen label als User-Bubble, senden aber query ans Backend.
    if (s.type === 'suggest_similar') {
      onQuestion?.(s.query || s.label, s.label);
      return;
    }
    // ask_colleague: Frage mit __COLLEAGUE_NEEDED__:-Prefix senden → Backend leitet direkt weiter
    if (s.type === 'ask_colleague') {
      onQuestion?.(s.query || s.label);
      return;
    }
    if (s.type === 'open_document') {
      const url = findBestDocumentUrl(s.label);
      if (url) onNavigate?.(url);
      return;
    }
    if (s.type === 'open_video') {
      const url = linkMap['video_transcript'] || null;
      if (url) onNavigate?.(url);
      return;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        marginTop: 10,
        paddingTop: 10,
        borderTop: '1px solid #F3F4F6',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {/* Intro label */}
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: '#9CA3AF',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          userSelect: 'none',
        }}
      >
        {introLabel}
      </span>

      {/* Chips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {suggestions.map((s, i) => {
          const cfg = TYPE_CONFIG[s.type] ?? TYPE_CONFIG.question;

          // Resolve whether this chip is actionable
          let actionable = true;
          // Im Clarification-Modus: question-Chips sind reine Hinweise, nicht klickbar
          if (s.type === 'question' && isClarificationMode) actionable = false;
          if (s.type === 'open_document' && !linkMap['document'] && !linkMap['quiz']) {
            actionable = false;
          }
          if (s.type === 'open_video' && !linkMap['video_transcript']) {
            actionable = false;
          }
          // select_variant, suggest_similar, ask_colleague sind immer klickbar

          return (
            <ChipButton
              key={i}
              cfg={cfg}
              label={s.label}
              actionable={actionable}
              onClick={() => handleChipClick(s)}
            />
          );
        })}
      </div>
    </div>
  );
}

function ChipButton({ cfg, label, actionable, onClick }) {
  const [hovered, setHovered] = React.useState(false);

  const style = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 11px',
    borderRadius: 10,
    border: `1px solid ${hovered && actionable ? cfg.color : cfg.border}`,
    background: hovered && actionable ? cfg.hoverBg : cfg.bg,
    color: cfg.color,
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1.35,
    cursor: actionable ? 'pointer' : 'default',
    opacity: actionable ? 1 : 0.45,
    textAlign: 'left',
    transition: 'background 0.15s ease, border-color 0.15s ease',
    userSelect: 'none',
  };

  return (
    <button
      data-chip
      style={style}
      onClick={actionable ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{cfg.icon}</span>
      <span>{label}</span>
    </button>
  );
}
